/**
 * 請求書一覧 `/invoices`。
 *
 * 「未払いと回収不能をすぐ見つける」ことが目的なので、状態列を金額のすぐ隣に置く。
 * 月次締めのプレビューは上部のパネルから実行する（実行前に必ず件数と金額を見せる）。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banknote, CalendarClock, Download, FileText, MoreHorizontal, TrendingUp } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  Textarea,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { INVOICE_STATUS, labelOf } from '@mail-sv/shared';
import { api, openPresigned } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { formatJpy, formatYearMonth, recentMonthOptions } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { hasActiveFilter } from '../lib/search.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { FilterBar, optionsFrom } from '../components/filter-bar.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { PageTabs } from '../components/page-tabs.js';
import { ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import { paidTotalOf, remainingOf } from './invoice-detail.js';
import type {
  ArAgingResponse,
  ArAgingRow,
  BillingSummaryMonth,
  BillingSummaryResponse,
  BillingSummaryTaxRow,
  InvoiceDetail,
  InvoiceSummary,
  PaymentEntry,
} from '../types/api.js';

const routeApi = getRouteApi(RID.invoices);

export function InvoicesPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader
        title="請求・入金"
        description="発行済みの請求書、受け取った入金、そして回収できていない売掛。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'invoices', label: '請求書', icon: FileText },
          { value: 'payments', label: '入金', icon: Banknote },
          { value: 'aging', label: '売掛（年齢調べ）', icon: CalendarClock },
          { value: 'summary', label: '売上サマリ', icon: TrendingUp },
        ]}
      />

      {search.tab === 'invoices' ? <InvoicesTab /> : null}
      {search.tab === 'payments' ? <PaymentsTab /> : null}
      {search.tab === 'aging' ? <AgingTab /> : null}
      {search.tab === 'summary' ? <SummaryTab /> : null}
    </>
  );
}

/* ------------------------------------------------------------- 請求書 */

function InvoicesTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canWrite = usePermission(OPS_PERMISSION.billingWrite);
  const [voidTarget, setVoidTarget] = React.useState<InvoiceSummary | null>(null);

  const [payTarget, setPayTarget] = React.useState<InvoiceSummary | null>(null);

  const list = useCursorList<InvoiceSummary>({
    path: '/invoices',
    keyFor: (params) => qk.invoices(params),
    /*
     * **サーバーが解釈するものだけを送る。**
     * `GET /ops/invoices` は `status` / `organizationId` / `q` / `periodYm` /
     * `sort` / `order`。`q` と `periodYm` は後から API に入ったのに画面が
     * 送っておらず、督促で「先月ぶんの未収だけ」を出せなかった。
     */
    filters: {
      status: search.status,
      organizationId: search.organizationId,
      q: search.q,
      periodYm: search.periodYm,
      sort: search.sort,
      order: search.order,
    },
  });

  const setSearch = (patch: Partial<typeof search>): void => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  };

  /** 絞り込みを外した既定の検索条件（並べ替えはサーバーの既定に戻す）。 */
  const clearedSearch = { tab: 'invoices', sort: 'id', order: 'desc' } as const;

  const columns: Array<DataTableColumn<InvoiceSummary>> = [
    {
      id: 'number',
      header: '請求書番号',
      cell: (i) => (
        <Link to="/invoices/$invoiceId" params={{ invoiceId: i.id }} className="font-mono font-medium hover:text-primary">
          {i.number}
        </Link>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      cell: (i) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: i.organizationId }}
          search={{ tab: 'invoices' }}
          className="hover:text-primary"
        >
          {i.organizationName}
        </Link>
      ),
    },
    {
      id: 'period',
      header: '対象期間',
      hideBelow: 'md',
      cell: (i) => `${formatDateTime(i.periodStart)} 〜 ${formatDateTime(i.periodEnd)}`,
    },
    {
      id: 'total',
      header: '金額',
      align: 'right',
      // サーバー側 keyset ソート。「高額の未収から潰す」は督促の実務そのもの。
      sortable: true,
      cell: (i) => formatJpy(i.totalJpy),
    },
    { id: 'status', header: '状態', cell: (i) => <StatusBadge status={i.status} size="sm" /> },
    { id: 'issuedAt', header: '発行日', hideBelow: 'lg', cell: (i) => formatDateTime(i.issuedAt) },
    { id: 'dueAt', header: '支払期限', hideBelow: 'md', sortable: true, cell: (i) => formatDateTime(i.dueAt) },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-12',
      align: 'right',
      cell: (i) => (
        <InvoiceRowActions
          invoice={i}
          canWrite={canWrite}
          onVoid={setVoidTarget}
          onRecordPayment={setPayTarget}
        />
      ),
    },
  ];

  return (
    <>
      <div className="mb-3 flex justify-end">
        <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
      </div>

      {canWrite ? <ClosePreviewPanel /> : null}

      {/*
        **請求書番号は検索対象ではない。** `GET /ops/invoices` の `q` は
        組織の slug / 表示名 / 請求先メールにしか当たらない（billing.ts:894）。
        出来ないことを placeholder に書くと「壊れている」と読まれるので、
        実際に効く語だけを書く。
      */}
      <FilterBar
        q={search.q}
        onQChange={(q) => setSearch({ q })}
        placeholder="契約組織 / slug / 請求先メールで絞り込む"
        active={hasActiveFilter(search)}
        onClear={() => void navigate({ search: clearedSearch })}
        selects={[
          {
            key: 'status',
            label: '状態',
            value: search.status,
            options: optionsFrom(INVOICE_STATUS, labelOf),
            onChange: (v) => setSearch({ status: v as typeof search.status }),
          },
          {
            key: 'periodYm',
            label: '対象年月',
            value: search.periodYm,
            /*
             * **直近 13 か月だけを出す。** 自由入力にすると `yyyy-MM` を打たれて
             * サーバーが 400 を返す（年月表記はリポジトリ全体で `yyyyMM`）。
             * 13 か月なのは「前年同月と比べる」がいちばん多い使い方だから。
             */
            options: recentMonthOptions(13),
            onChange: (v) => setSearch({ periodYm: v }),
          },
        ]}
      />

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(i) => i.id}
        loading={list.query.isLoading}
        sort={{ columnId: search.sort, direction: search.order }}
        onSortChange={(next) => setSearch({ sort: next.columnId as typeof search.sort, order: next.direction })}
        empty={
          <ListEmpty
            filtered={hasActiveFilter(search)}
            entityLabel="請求書"
            onClearFilter={() => void navigate({ search: clearedSearch })}
          />
        }
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <VoidInvoiceDialog invoice={voidTarget} onClose={() => setVoidTarget(null)} />
      <RecordPaymentDialog invoice={payTarget} onClose={() => setPayTarget(null)} />
    </>
  );
}

/* ------------------------------------------------------------- 月次締め */

/**
 * `GET /ops/billing/close-preview` の 1 契約ぶんの内訳（A243）。
 *
 * **合計だけでは「なぜ先月と違うのか」に答えられない。** 締めを実行する前に
 * 止められるのはこの画面だけなので、基本料・従量・調整・SLA クレジットを
 * 分けて出す。金額は API 側が `generateInvoice` と同じ純関数で算出した値を
 * そのまま出す（**画面で足し引きしない**。丸めが税率ごとに 1 回という
 * インボイス制度の要件は `billing-math.ts` が守っている）。
 */
interface ClosePreviewItem {
  organizationId: string;
  organizationName: string;
  planName: string;
  quantity: number;
  billingPeriod: string;
  billedThisMonth: boolean;
  alreadyIssued: boolean;
  baseAmount: number;
  overageAmount: number;
  adjustmentAmount: number;
  /** SLA クレジット。**負ではなく正の値で返る**（差し引く額そのもの）。 */
  creditAmount: number;
  subtotal: number;
  taxAmount: number;
  total: number;
  prorated: boolean;
  transitional: boolean;
}

/** `GET /ops/billing/close-preview` の応答（画面が直接読む平たいキーのぶん）。 */
interface ClosePreview {
  periodLabel: string;
  invoiceCount: number;
  /** **税込**の見込み合計。`generateInvoice` と同じ純関数で出している。 */
  totalJpy: number;
  subtotalJpy: number;
  taxJpy: number;
  unfinalized: number;
  blockers: string[];
  items: ClosePreviewItem[];
}

function ClosePreviewPanel(): React.ReactElement | null {
  const qc = useQueryClient();
  const preview = useQuery({
    queryKey: qk.closePreview(),
    queryFn: () => api.get<ClosePreview>('/billing/close-preview'),
  });

  const close = useMutation({
    mutationFn: () => api.post('/billing/close', {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invoices() });
      void qc.invalidateQueries({ queryKey: qk.closePreview() });
    },
  });

  const data = preview.data;
  if (!data || data.invoiceCount === 0) return null;

  return (
    <Section className="mb-5">
      <Alert variant={data.blockers.length > 0 ? 'warning' : 'info'}>
        <AlertTitle className="flex items-center gap-2">
          <CalendarClock className="size-4" aria-hidden />
          {data.periodLabel} の締めが未実行です
        </AlertTitle>
        <AlertDescription className="space-y-2">
          {/*
            金額は**税込の見込み額**。日割り・従量・SLA クレジットまで含めて
            `generateInvoice` と同じ関数で出しているので、合計だけでなく
            小計と消費税も並べて「なぜこの額なのか」を追えるようにする。
          */}
          <p>
            発行予定 {formatCount(data.invoiceCount)} 件 / 合計{' '}
            <strong className="tabular">{formatJpy(data.totalJpy)}</strong>（税込）
          </p>
          <p className="text-xs text-muted-foreground tabular">
            小計 {formatJpy(data.subtotalJpy)} + 消費税 {formatJpy(data.taxJpy)}
            {data.unfinalized > 0 ? ` / 使用量が未確定の日 ${formatCount(data.unfinalized)} 件` : ''}
          </p>
          {data.blockers.length > 0 ? (
            <ul className="list-inside list-disc text-xs">
              {data.blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          ) : null}
          <ClosePreviewBreakdown items={data.items ?? []} />
          <Button
            size="sm"
            onClick={() => close.mutate()}
            loading={close.isPending}
            disabled={data.blockers.length > 0}
          >
            締めを実行する
          </Button>
        </AlertDescription>
      </Alert>
    </Section>
  );
}

/**
 * 発行予定の内訳（A243）。
 *
 * 既定では畳んでおく。締めは「合計が想定どおりか」を見て押す操作で、
 * 毎回 60 契約ぶんの表を読ませると肝心のブロッカーが下に押し出される。
 * 想定と違ったときだけ開いて、どの契約で差が出ているかを見る。
 *
 * **まだ発行していない契約だけ**を並べる（`alreadyIssued` は締めても
 * 何も起きないので、並べると件数が合わなくなる）。
 */
function ClosePreviewBreakdown({ items }: { items: ClosePreviewItem[] }): React.ReactElement | null {
  const pending = items.filter((i) => !i.alreadyIssued && i.billedThisMonth);
  if (pending.length === 0) return null;

  return (
    <details className="rounded-md border border-border bg-panel-elevated">
      <summary className="cursor-pointer rounded-md px-3 py-2 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        発行予定の内訳を見る（{formatCount(pending.length)} 契約）
      </summary>
      <div className="overflow-x-auto px-3 pb-3">
        <table className="w-full min-w-[46rem] text-xs">
          <thead className="text-muted-foreground">
            <tr className="border-b border-border">
              <th className="py-1.5 text-left font-medium">契約組織</th>
              <th className="py-1.5 text-left font-medium">プラン</th>
              <th className="py-1.5 text-right font-medium">基本料</th>
              <th className="py-1.5 text-right font-medium">従量</th>
              <th className="py-1.5 text-right font-medium">調整</th>
              <th className="py-1.5 text-right font-medium">SLA</th>
              <th className="py-1.5 text-right font-medium">小計</th>
              <th className="py-1.5 text-right font-medium">消費税</th>
              <th className="py-1.5 text-right font-medium">合計</th>
            </tr>
          </thead>
          <tbody>
            {pending.map((i) => (
              <tr key={i.organizationId} className="border-b border-border last:border-0">
                <td className="py-1.5">
                  <Link
                    to="/organizations/$orgId"
                    params={{ orgId: i.organizationId }}
                    search={{ tab: 'invoices' }}
                    className="hover:text-primary"
                  >
                    {i.organizationName}
                  </Link>
                </td>
                <td className="py-1.5 text-muted-foreground">
                  {i.planName}
                  {i.quantity > 1 ? ` ×${i.quantity}` : ''}
                  {/* 日割り・移行期は金額が「いつもと違う」理由そのものなので必ず出す。 */}
                  {i.prorated ? <span className="ml-1 text-warning">日割り</span> : null}
                  {i.transitional ? <span className="ml-1 text-warning">移行</span> : null}
                </td>
                <td className="py-1.5 text-right tabular">{formatJpy(i.baseAmount)}</td>
                <td className="py-1.5 text-right tabular">{i.overageAmount ? formatJpy(i.overageAmount) : '—'}</td>
                <td className="py-1.5 text-right tabular">
                  {i.adjustmentAmount ? formatJpy(i.adjustmentAmount) : '—'}
                </td>
                <td className="py-1.5 text-right tabular text-success">
                  {/* クレジットは正の値で返るので、差し引きであることを符号で示す。 */}
                  {i.creditAmount ? `−${formatJpy(i.creditAmount)}` : '—'}
                </td>
                <td className="py-1.5 text-right tabular">{formatJpy(i.subtotal)}</td>
                <td className="py-1.5 text-right tabular">{formatJpy(i.taxAmount)}</td>
                <td className="py-1.5 text-right font-medium tabular">{formatJpy(i.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/* -------------------------------------------------------------- 行アクション */

function InvoiceRowActions({
  invoice,
  canWrite,
  onVoid,
  onRecordPayment,
}: {
  invoice: InvoiceSummary;
  canWrite: boolean;
  onVoid: (i: InvoiceSummary) => void;
  onRecordPayment: (i: InvoiceSummary) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.post(`/invoices/${invoice.id}/retry-payment`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.invoices() }),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`${invoice.number} の操作`}>
          <MoreHorizontal aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem asChild>
          <Link to="/invoices/$invoiceId" params={{ invoiceId: invoice.id }}>
            詳細を開く
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => void openPresigned(`/invoices/${invoice.id}/pdf`)}>
          <Download className="size-4" aria-hidden />
          PDF を開く
        </DropdownMenuItem>
        {/*
          **`open` だけに絞らない。** 督促を尽くして貸倒（`uncollectible`）に
          倒した請求書に、あとから入金があることは実際に起きる。以前は
          その状態で入金を記録する導線が画面から消えており、いちばん要る
          場面で使えなかった。サーバー（`mark-paid`）が拒むのは
          `paid` と `void` の 2 つだけなので、画面もそれに合わせる。
        */}
        {canWrite && (invoice.status === 'open' || invoice.status === 'uncollectible') ? (
          <DropdownMenuItem onSelect={() => onRecordPayment(invoice)}>
            <Banknote className="size-4" aria-hidden />
            入金を登録して消し込む
          </DropdownMenuItem>
        ) : null}
        {canWrite && invoice.status === 'open' ? (
          <DropdownMenuItem onSelect={() => retry.mutate()} disabled={retry.isPending}>
            決済を再試行
          </DropdownMenuItem>
        ) : null}
        {canWrite && invoice.status !== 'paid' && invoice.status !== 'void' ? (
          <DropdownMenuItem className="text-destructive" onSelect={() => onVoid(invoice)}>
            無効化する
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * 手動入金の登録（`POST /ops/invoices/:invoiceId/mark-paid`）。
 *
 * **ここが以前は「必ず 400 で失敗するボタン」だった。**
 * `POST /payments/:paymentId/reconcile` に**請求書 ID を paymentId として**渡し、
 * 必須の `{invoiceId}` も送っていなかった（消し込みたい入金がまだ存在しないので、
 * そもそも reconcile は使えない）。`BILLING_PROVIDER=manual` では `payments` に
 * 行を作る経路がここしか無く、**正しく振り込んだ顧客が督促され続けて
 * 44 日目に自動停止する**という結果になる。
 *
 * その後 `POST /payments`（組織 ID と金額を画面が組み立てる汎用の入金登録）へ
 * 直したが、**既定額を請求満額に固定していた**ため、一部入金済みの請求書では
 * 「残額」ではなく「満額」が初期値になり、押した運営が過入金を作れてしまった。
 *
 * いま使うのは請求書専用の `mark-paid`。
 *   - 金額を省略すると**サーバーが残額を採る**（分割入金の 2 回目が正しくなる）
 *   - 総額以上の入金が既にあれば 409 で止まる
 *   - `void` / `paid` の請求書は受け付けない
 * 画面はそれを写経せず、**残額を初期値として見せるためだけに**請求書詳細
 * （`GET /ops/invoices/:invoiceId`）を引く。判定はサーバーが正。
 */
function RecordPaymentDialog({
  invoice,
  onClose,
}: {
  invoice: InvoiceSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [amount, setAmount] = React.useState('');
  const [method, setMethod] = React.useState('bank_transfer');
  const [paidOn, setPaidOn] = React.useState('');
  const [payerName, setPayerName] = React.useState('');
  const [externalPaymentId, setExternalPaymentId] = React.useState('');
  const [note, setNote] = React.useState('');

  /*
   * 既存の入金を見て残額を出す。**失敗しても登録はできる**（金額欄が
   * 空のままでもサーバーが残額を採るため）。ここで詰まらせない。
   */
  const detail = useQuery({
    queryKey: qk.invoice(invoice?.id ?? ''),
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${invoice?.id ?? ''}`),
    enabled: invoice !== null,
  });
  // **請求書詳細と同じ式を使う。** 別々に書くと片方だけ返金を引き忘れて、
  // 同じ請求書の残額が画面によって違う値になる（実際に起きうる形）。
  const paidTotal = paidTotalOf(detail.data?.payments ?? []);
  const remaining = invoice ? remainingOf(invoice.totalJpy, paidTotal) : 0;

  React.useEffect(() => {
    if (!invoice) return;
    setMethod('bank_transfer');
    setPaidOn(new Date().toISOString().slice(0, 10));
    setPayerName('');
    setExternalPaymentId('');
    setNote('');
  }, [invoice]);

  // 残額が分かった時点で初期値にする（引けなければ空のまま＝サーバー既定）。
  React.useEffect(() => {
    if (detail.data) setAmount(String(remainingOf(detail.data.totalJpy, paidTotal)));
  }, [detail.data, paidTotal]);

  const mut = useMutation({
    mutationFn: () =>
      api.post<{ paymentId: string; amount: number; invoicePaid: boolean; paidTotal: number }>(
        `/invoices/${invoice?.id ?? ''}/mark-paid`,
        {
          method,
          // 空欄なら送らない＝サーバーが残額を採る。
          ...(amount.trim() === '' ? {} : { amount: Number(amount) }),
          // 日付だけを選ばせて、時刻は当日の 0 時（ローカル）にする。
          // API は ISO 8601（datetime）しか受け取らない。
          paidAt: paidOn ? new Date(`${paidOn}T00:00:00`).toISOString() : undefined,
          payerName: payerName.trim() === '' ? undefined : payerName.trim(),
          externalPaymentId: externalPaymentId.trim() === '' ? undefined : externalPaymentId.trim(),
          note: note.trim() === '' ? undefined : note.trim(),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invoices() });
      void qc.invalidateQueries({ queryKey: ['ops', 'payments'] });
      if (invoice) void qc.invalidateQueries({ queryKey: qk.invoice(invoice.id) });
      onClose();
    },
  });

  if (!invoice) return null;

  const amountValue = Number(amount);
  const amountInvalid = amount.trim() !== '' && (!Number.isInteger(amountValue) || amountValue <= 0);

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${invoice.number} の入金を登録`}
      description={`${invoice.organizationName} への請求 ${formatJpy(invoice.totalJpy)} に対する入金を記録します。満額に達した時点で請求書が「入金済み」になり、未納による停止も解除されます。`}
      submitLabel="入金を登録する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={amountInvalid}
      onSubmit={() => mut.mutate()}
    >
      {paidTotal > 0 ? (
        <Alert variant="info">
          <AlertDescription>
            この請求書には既に {formatJpy(paidTotal)} の入金があります（残額 {formatJpy(remaining)}）。
            初期値は残額です。
          </AlertDescription>
        </Alert>
      ) : null}

      <Field
        label="入金額（円）"
        required
        hint="消費税込みの実入金額。空欄のまま登録すると残額の全額として記録します。分割入金は 1 回ずつ登録してください（サーバー側で合算します）。"
        error={amountInvalid ? '1 円以上の整数で入力してください。' : undefined}
      >
        <Input
          type="number"
          min={1}
          step={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="numeric"
        />
      </Field>

      <Field label="入金方法">
        <NativeSelect
          value={method}
          onChange={setMethod}
          options={[
            { value: 'bank_transfer', label: '銀行振込' },
            { value: 'card', label: 'クレジットカード' },
            { value: 'invoice', label: '請求書払い' },
            { value: 'manual', label: 'その他（現金など）' },
          ]}
        />
      </Field>

      <Field label="入金日" hint="通帳・入出金明細の日付。未指定なら受付時刻になります。">
        <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
      </Field>

      <Field label="振込名義" hint="通帳に記載された名義。突合のために残しておくと後から追えます。">
        <Input value={payerName} onChange={(e) => setPayerName(e.target.value)} />
      </Field>

      <Field
        label="取引 ID"
        hint="銀行の取引 ID。入れておくと二重登録をサーバー側で弾けます（空欄でも登録できますが、その場合は重複を防げません）。"
      >
        <Input
          value={externalPaymentId}
          onChange={(e) => setExternalPaymentId(e.target.value)}
          spellCheck={false}
        />
      </Field>

      <Field label="備考">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/**
 * 請求書の無効化（`POST /ops/invoices/:invoiceId/void`）。
 *
 * **返還請求書（赤伝）と混同しないこと。** 交付前・未入金の訂正がこちらで、
 * 交付済みのものは請求書詳細から返還請求書を切る。詳細画面と同じ理由を
 * ここでも受け取る（`invoices.notes` と監査ログに残る）。理由の無い
 * 無効化は、後から「なぜ消えたのか」を誰も説明できない証憑を作る。
 */
function VoidInvoiceDialog({
  invoice,
  onClose,
}: {
  invoice: InvoiceSummary | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (invoice) setReason('');
  }, [invoice]);

  const mut = useMutation({
    mutationFn: () =>
      api.post(`/invoices/${invoice?.id ?? ''}/void`, {
        ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.invoices() });
      if (invoice) void qc.invalidateQueries({ queryKey: qk.invoice(invoice.id) });
      onClose();
    },
  });

  if (!invoice) return null;

  return (
    <ConfirmTextDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="請求書を無効化"
      description="「発行しなかったこと」にします。凍結していた日次集計と基本料の前払い終端が発行前へ戻るので、締め直せば作り直せます。既に契約者へ渡した請求書は、無効化ではなく返還請求書（詳細画面）で訂正してください。"
      confirmText={invoice.number}
      confirmLabel="無効化する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      onConfirm={() => mut.mutate()}
    >
      <Field label="理由" hint="請求書のメモと監査ログに残ります（500 文字まで）。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="対象期間の指定を誤って発行したため（交付前・未入金）"
        />
      </Field>
    </ConfirmTextDialog>
  );
}

/* --------------------------------------------------------------- 入金 */

/**
 * 入金の横断一覧（R86）。
 *
 * `GET /ops/payments` は R0 から在るのに参照する画面が無く、
 * **「振り込まれているのに、どの請求書にも当たっていない入金」**が
 * 誰にも見えていなかった。未消込のまま放置すると請求書は `open` のままで、
 * 督促（`handleDunning`）が毎朝走り、期限超過 14 日で送信が止まる。
 *
 * 未消込は warning 色のバッジで示し、その場で充当先を選んで消し込める。
 */
function PaymentsTab(): React.ReactElement {
  const canWrite = usePermission(OPS_PERMISSION.billingWrite);
  const [reconcileTarget, setReconcileTarget] = React.useState<PaymentEntry | null>(null);
  const [recordOpen, setRecordOpen] = React.useState(false);

  const list = useCursorList<PaymentEntry>({
    path: '/payments',
    keyFor: (params) => qk.payments(params),
    // サーバー側にフィルタが 1 つも無い（cursor と limit だけ）。
    // 受け取らない値を送っても黙って無視されるだけなので、UI にも出さない。
    filters: {},
  });

  const unreconciled = list.rows.filter((p) => p.reconciledAt === null);
  const unreconciledTotal = unreconciled.reduce((sum, p) => sum + p.amount, 0);

  const columns: Array<DataTableColumn<PaymentEntry>> = [
    {
      id: 'paidAt',
      header: '入金日',
      cell: (p) => (
        <div className="min-w-0">
          <span className="block">{formatDateTime(p.paidAt ?? p.createdAt)}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {p.payerName ?? '振込名義なし'}
          </span>
        </div>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      cell: (p) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: p.organizationId }}
          search={{ tab: 'invoices' }}
          className="font-mono text-xs hover:text-primary"
        >
          {p.organizationId}
        </Link>
      ),
    },
    { id: 'amount', header: '金額', align: 'right', cell: (p) => formatJpy(p.amount) },
    { id: 'method', header: '方法', hideBelow: 'md', cell: (p) => labelOf(p.method) },
    { id: 'status', header: '状態', cell: (p) => <StatusBadge status={p.status} size="sm" /> },
    {
      id: 'invoice',
      header: '充当先',
      cell: (p) =>
        p.invoiceId ? (
          <Link
            to="/invoices/$invoiceId"
            params={{ invoiceId: p.invoiceId }}
            className="font-mono text-xs hover:text-primary"
          >
            {p.invoiceId}
          </Link>
        ) : (
          <Badge variant="warning" size="sm">
            未消込
          </Badge>
        ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      width: 'w-28',
      align: 'right',
      cell: (p) =>
        canWrite && p.reconciledAt === null ? (
          <Button variant="outline" size="sm" onClick={() => setReconcileTarget(p)}>
            消し込む
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      {canWrite ? (
        <div className="mb-3 flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setRecordOpen(true)}>
            <Banknote aria-hidden />
            入金を記録する
          </Button>
        </div>
      ) : null}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard label="表示中の入金" value={formatCount(list.rows.length)} icon={Banknote} />
        <StatCard
          label="未消込"
          value={formatCount(unreconciled.length)}
          tone={unreconciled.length > 0 ? 'warning' : 'success'}
          hint={
            unreconciled.length > 0
              ? `${formatJpy(unreconciledTotal)} がどの請求書にも当たっていません`
              : 'すべて請求書に充当済みです'
          }
        />
      </div>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(p) => p.id}
        loading={list.query.isLoading}
        emptyTitle="入金の記録がありません"
        emptyDescription="請求書に対する入金は請求書タブの行メニューから、どの請求書にも当たらない入金はこの画面の「入金を記録する」から登録します。"
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <ReconcileDialog payment={reconcileTarget} onClose={() => setReconcileTarget(null)} />
      <RecordStandalonePaymentDialog open={recordOpen} onOpenChange={setRecordOpen} />
    </>
  );
}

/**
 * 請求書に紐づかない入金の記録（`POST /ops/payments`）。
 *
 * **請求書からの消し込み（`mark-paid`）とは用途が違う。** こちらは
 * 「振り込まれたが、どの請求書のものか分からない」「前受金として先に
 * 受け取った」入金を台帳へ載せるための入口で、充当は後から
 * `POST /payments/:paymentId/reconcile`（一覧の「消し込む」）で行う。
 *
 * この口が画面から消えていた間、`BILLING_PROVIDER=manual` では
 * **未消込の入金を作る手段そのものが無く**、上の「未消込」カードと
 * 消し込みボタンが実質デッドコードになっていた。
 *
 * 組織 ID はテキスト入力（sla-credits の起票と同じ作法）。組織一覧から
 * コピーして貼る運用で、サーバーが存在確認する。
 */
function RecordStandalonePaymentDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [organizationId, setOrganizationId] = React.useState('');
  const [amount, setAmount] = React.useState('');
  const [method, setMethod] = React.useState('bank_transfer');
  const [paidOn, setPaidOn] = React.useState('');
  const [payerName, setPayerName] = React.useState('');
  const [externalPaymentId, setExternalPaymentId] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setOrganizationId('');
    setAmount('');
    setMethod('bank_transfer');
    setPaidOn(new Date().toISOString().slice(0, 10));
    setPayerName('');
    setExternalPaymentId('');
    setNote('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post<{ paymentId: string }>('/payments', {
        organizationId: organizationId.trim(),
        method,
        amount: Number(amount),
        paidAt: paidOn ? new Date(`${paidOn}T00:00:00`).toISOString() : undefined,
        payerName: payerName.trim() === '' ? undefined : payerName.trim(),
        externalPaymentId: externalPaymentId.trim() === '' ? undefined : externalPaymentId.trim(),
        note: note.trim() === '' ? undefined : note.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'payments'] });
      void qc.invalidateQueries({ queryKey: qk.invoices() });
      onOpenChange(false);
    },
  });

  const amountValue = Number(amount);
  const amountInvalid = amount.trim() === '' || !Number.isInteger(amountValue) || amountValue <= 0;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="入金を記録する（請求書に紐づけない）"
      description="どの請求書のものか分からない入金を、いったん台帳へ載せます。充当先が分かったら一覧の「消し込む」から請求書へ紐づけてください。"
      submitLabel="記録する"
      pending={submit.isPending}
      error={submit.error}
      disabled={organizationId.trim().length === 0 || amountInvalid}
      onSubmit={() => submit.mutate()}
    >
      <Field label="契約組織 ID" required hint="契約組織の一覧からコピーしてください。">
        <Input
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          spellCheck={false}
          className="font-mono"
        />
      </Field>

      <Field
        label="入金額（円）"
        required
        hint="消費税込みの実入金額。"
        error={amount.trim() !== '' && amountInvalid ? '1 円以上の整数で入力してください。' : undefined}
      >
        <Input
          type="number"
          min={1}
          step={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputMode="numeric"
        />
      </Field>

      <Field label="入金方法">
        <NativeSelect
          value={method}
          onChange={setMethod}
          options={[
            { value: 'bank_transfer', label: '銀行振込' },
            { value: 'card', label: 'クレジットカード' },
            { value: 'invoice', label: '請求書払い' },
            { value: 'manual', label: 'その他（現金など）' },
          ]}
        />
      </Field>

      <Field label="入金日" hint="通帳・入出金明細の日付。未指定なら受付時刻になります。">
        <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
      </Field>

      <Field label="振込名義" hint="通帳に記載された名義。充当先を探すときの手掛かりになります。">
        <Input value={payerName} onChange={(e) => setPayerName(e.target.value)} />
      </Field>

      <Field label="取引 ID" hint="銀行の取引 ID。入れておくと二重登録をサーバー側で弾けます。">
        <Input
          value={externalPaymentId}
          onChange={(e) => setExternalPaymentId(e.target.value)}
          spellCheck={false}
        />
      </Field>

      <Field label="備考">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/**
 * 未消込の入金を請求書へ充当する（`POST /ops/payments/:paymentId/reconcile`）。
 *
 * **`paymentId` はパス、`invoiceId` は body。** ここを取り違えた実装が
 * 長く残っていて（請求書 ID を paymentId に渡し、必須の body を送っていなかった）、
 * 消し込みボタンは押すたびに 400 を返していた。
 *
 * 充当先は「同じ組織の未払い請求書」からしか選ばせない。サーバーも
 * 組織不一致を 400 で弾くが、選ばせてから弾くのは操作として最悪なので、
 * 候補の時点で絞る。
 */
function ReconcileDialog({
  payment,
  onClose,
}: {
  payment: PaymentEntry | null;
  onClose: () => void;
}): React.ReactElement | null {
  const qc = useQueryClient();
  const [invoiceId, setInvoiceId] = React.useState('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    setInvoiceId('');
    setNote('');
  }, [payment?.id]);

  // 同じ組織の未払い請求書だけを候補にする。
  const candidates = useQuery({
    queryKey: qk.invoices({ organizationId: payment?.organizationId ?? '', status: 'open' }),
    queryFn: () =>
      api.get<{ items: InvoiceSummary[] }>('/invoices', {
        organizationId: payment?.organizationId,
        status: 'open',
        limit: 100,
      }),
    enabled: payment !== null,
  });

  const mut = useMutation({
    mutationFn: () =>
      api.post(`/payments/${payment?.id ?? ''}/reconcile`, {
        invoiceId,
        note: note.trim() === '' ? undefined : note.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'payments'] });
      void qc.invalidateQueries({ queryKey: qk.invoices() });
      onClose();
    },
  });

  if (!payment) return null;

  const options = (candidates.data?.items ?? []).map((i) => ({
    value: i.id,
    label: `${i.number} — ${formatJpy(i.totalJpy)}（期限 ${formatDateTime(i.dueAt)}）`,
  }));

  return (
    <FormDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="入金を請求書に充当"
      description={`${formatJpy(payment.amount)} の入金を、この契約組織の未払い請求書に紐づけます。合算で満額に達した請求書はサーバー側で自動的に支払い済みになります。`}
      submitLabel="充当する"
      pending={mut.isPending}
      disabled={invoiceId === ''}
      error={mut.isError ? mut.error : undefined}
      onSubmit={() => mut.mutate()}
    >
      <Field label="充当先の請求書" hint="同じ契約組織の未払い（open）請求書だけが選べます。">
        {candidates.isLoading ? (
          <p className="text-sm text-muted-foreground">読み込み中…</p>
        ) : options.length === 0 ? (
          <Alert variant="destructive">
            <AlertDescription>
              この契約組織に未払いの請求書がありません。先に月次締めで請求書を発行してください。
            </AlertDescription>
          </Alert>
        ) : (
          <NativeSelect
            value={invoiceId}
            onChange={setInvoiceId}
            options={[{ value: '', label: '請求書を選ぶ' }, ...options]}
          />
        )}
      </Field>
      <Field label="メモ" hint="任意。振込の照合に使った情報（振込人名義の相違など）を残せます。">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
      </Field>
    </FormDialog>
  );
}

/* ----------------------------------------------------------- 売掛の年齢調べ */

/** エイジングの区分。**順序が意味を持つ**ので配列で持つ（オブジェクトのキー順に依存しない）。 */
const AGING_BUCKETS = [
  { key: 'notDue', label: '期限内', tone: '' },
  { key: 'd0_30', label: '1〜30 日', tone: 'text-warning' },
  { key: 'd31_60', label: '31〜60 日', tone: 'text-warning' },
  { key: 'd61_90', label: '61〜90 日', tone: 'text-destructive' },
  { key: 'over90', label: '91 日以上', tone: 'text-destructive' },
] as const;

/**
 * 売掛の年齢調べ（A69）。
 *
 * 「未払いが何件あるか」は請求書一覧でも分かるが、**それが何日滞留しているか**は
 * 分からなかった。督促の強さ（メール → 電話 → 送信停止 → 契約解除）は滞留日数で
 * 決めるものなので、これが無いと運営は請求書を 1 枚ずつ開いて期限を見るしかない。
 *
 * 集計は `GET /ops/billing/aging` が SQL で行う（**画面で足し込まない**）。
 * 対象は `open` と `uncollectible` の請求書で、金額は税込の残高。
 */
function AgingTab(): React.ReactElement {
  const query = useQuery({
    queryKey: qk.billingAging(),
    queryFn: () => api.get<ArAgingResponse>('/billing/aging'),
  });

  const data = query.data;
  const rows = data?.items ?? [];

  const columns: Array<DataTableColumn<ArAgingRow>> = [
    {
      id: 'org',
      header: '契約組織',
      cell: (r) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: r.organizationId }}
          search={{ tab: 'invoices' }}
          className="font-medium hover:text-primary"
        >
          {r.organizationName ?? '（削除済み組織）'}
        </Link>
      ),
    },
    {
      id: 'invoices',
      header: '件数',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => formatCount(r.invoices),
    },
    ...AGING_BUCKETS.map(
      (b): DataTableColumn<ArAgingRow> => ({
        id: b.key,
        header: b.label,
        align: 'right',
        // 0 円の区分は「—」にして、金額が入っているところだけを目で拾えるようにする。
        cell: (r) =>
          r.buckets[b.key] === 0 ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className={b.tone}>{formatJpy(r.buckets[b.key])}</span>
          ),
      }),
    ),
    {
      id: 'outstanding',
      header: '合計',
      align: 'right',
      cell: (r) => <span className="font-medium">{formatJpy(r.outstanding)}</span>,
    },
  ];

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-6">
        <StatCard
          label="未回収の合計"
          value={formatJpy(data?.totals.outstanding ?? 0)}
          icon={CalendarClock}
          tone={(data?.totals.over90 ?? 0) > 0 ? 'destructive' : 'default'}
        />
        {AGING_BUCKETS.map((b) => (
          <StatCard
            key={b.key}
            label={b.label}
            value={formatJpy(data?.totals[b.key] ?? 0)}
            tone={
              b.key === 'notDue' || (data?.totals[b.key] ?? 0) === 0
                ? 'default'
                : b.key === 'over90' || b.key === 'd61_90'
                  ? 'destructive'
                  : 'warning'
            }
          />
        ))}
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.organizationId}
        loading={query.isLoading}
        emptyTitle="未回収の請求書はありません"
        emptyDescription="発行済みの請求書がすべて支払い済み、または無効化されています。"
      />

      <p className="mt-3 text-xs text-muted-foreground">
        対象は状態が「未払い」「回収不能」の請求書です。区分は支払期限からの経過日数で、
        金額は税込の残高（上位 500 契約）。
      </p>
    </>
  );
}

/* --------------------------------------------------------- 売上サマリ */

/** 万分率の税率を「10%」の形にする。`taxRateBp = 1000` が 10%。 */
export function taxRateLabel(bp: number): string {
  const pct = bp / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)} %`;
}

/**
 * 月次の売上・実収サマリ（`GET /ops/billing/summary`）。
 *
 * **請求した額と入ってきた額は別物**で、月次締めのパネルは前者しか出さない。
 * 「先月いくら請求して、いくら回収できたか」を運営が知る手段が無く、
 * 消費税の申告に要る税率区分ごとの内訳もどこにも出ていなかった。
 *
 * 集計は**すべてサーバーの SQL**（`invoices` と `payments`）で、画面では
 * 足し込みも税率の掛け直しもしない。丸めは税率ごとに 1 回という
 * インボイス制度の要件（`billing-math.ts`）を画面が壊さないための規律。
 *
 * `collected` は**その月に入金された額**なので、同じ行の `total`（その月に
 * 請求した額）とは対応しない。並べて出すのは推移を見るためで、
 * 差額をその月の未回収額として読まないこと（それは売掛タブが正しい）。
 */
function SummaryTab(): React.ReactElement {
  const query = useQuery({
    queryKey: qk.billingSummary(),
    queryFn: () => api.get<BillingSummaryResponse>('/billing/summary'),
  });

  const data = query.data;
  // API は古い月から返す。運営が見たいのは直近なので、表示だけ逆順にする。
  const months = React.useMemo(() => [...(data?.months ?? [])].reverse(), [data?.months]);

  const totals = React.useMemo(
    () =>
      (data?.months ?? []).reduce(
        (acc, m) => ({
          total: acc.total + m.total,
          collected: acc.collected + m.collected,
          invoices: acc.invoices + m.invoices,
          uncollectible: acc.uncollectible + m.uncollectibleCount,
        }),
        { total: 0, collected: 0, invoices: 0, uncollectible: 0 },
      ),
    [data?.months],
  );

  const columns: Array<DataTableColumn<BillingSummaryMonth>> = [
    {
      id: 'month',
      header: '対象月',
      cell: (m) => <span className="tabular font-medium">{formatYearMonth(m.yearMonth)}</span>,
    },
    { id: 'invoices', header: '発行', align: 'right', hideBelow: 'md', cell: (m) => formatCount(m.invoices) },
    {
      id: 'subtotal',
      header: '小計（税抜）',
      align: 'right',
      hideBelow: 'lg',
      cell: (m) => <span className="tabular">{formatJpy(m.subtotal)}</span>,
    },
    {
      id: 'tax',
      header: '消費税',
      align: 'right',
      hideBelow: 'lg',
      cell: (m) => <span className="tabular">{formatJpy(m.tax)}</span>,
    },
    {
      id: 'total',
      header: '請求合計',
      align: 'right',
      cell: (m) => <span className="tabular font-medium">{formatJpy(m.total)}</span>,
    },
    {
      id: 'collected',
      header: '入金',
      align: 'right',
      cell: (m) => <span className="tabular">{formatJpy(m.collected)}</span>,
    },
    {
      id: 'open',
      header: '未払い',
      align: 'right',
      hideBelow: 'md',
      cell: (m) =>
        m.openCount === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="text-warning">{formatCount(m.openCount)}</span>
        ),
    },
    {
      id: 'uncollectible',
      header: '回収不能',
      align: 'right',
      hideBelow: 'md',
      cell: (m) =>
        m.uncollectibleCount === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="text-destructive">{formatCount(m.uncollectibleCount)}</span>
        ),
    },
  ];

  const taxColumns: Array<DataTableColumn<BillingSummaryTaxRow>> = [
    {
      id: 'month',
      header: '対象月',
      cell: (r) => <span className="tabular">{formatYearMonth(r.yearMonth)}</span>,
    },
    { id: 'rate', header: '税率', cell: (r) => taxRateLabel(r.taxRateBp) },
    {
      id: 'code',
      header: '区分',
      hideBelow: 'md',
      cell: (r) => (r.taxCode === '' ? '—' : labelOf(r.taxCode)),
    },
    {
      id: 'taxable',
      header: '課税対象額',
      align: 'right',
      cell: (r) => <span className="tabular">{formatJpy(r.taxable)}</span>,
    },
    {
      id: 'tax',
      header: '消費税額',
      align: 'right',
      cell: (r) => <span className="tabular">{formatJpy(r.tax)}</span>,
    },
  ];

  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="期間の請求合計" value={formatJpy(totals.total)} icon={TrendingUp} />
        <StatCard label="期間の入金合計" value={formatJpy(totals.collected)} icon={Banknote} />
        <StatCard label="発行した請求書" value={formatCount(totals.invoices)} unit="通" icon={FileText} />
        <StatCard
          label="回収不能"
          value={formatCount(totals.uncollectible)}
          unit="通"
          tone={totals.uncollectible > 0 ? 'destructive' : 'default'}
        />
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={months}
        rowKey={(m) => m.yearMonth}
        loading={query.isLoading}
        emptyTitle="集計できる請求書がありません"
        emptyDescription="発行済み（issued_at の入った）請求書だけを数えます。"
      />

      <Section
        className="mt-6"
        title="税率区分ごとの内訳"
        description="消費税の申告に使う数字です。丸めは税率ごとに 1 回だけ行われ、画面では掛け直していません。"
      >
        <DataTable
          columns={taxColumns}
          rows={data?.taxBreakdown ?? []}
          rowKey={(r) => `${r.yearMonth}-${r.taxRateBp}`}
          loading={query.isLoading}
          emptyTitle="税率区分の内訳がありません"
        />
      </Section>

      <p className="mt-3 text-xs text-muted-foreground">
        期間は {data ? `${formatYearMonth(data.from)} 〜 ${formatYearMonth(data.to)}` : '直近 12 か月'}
        （無効化した請求書は除く）。「入金」は<strong>その月に入金された額</strong>で、
        同じ行の請求合計に対応するものではありません。滞留の内訳は「売掛（年齢調べ）」を見てください。
      </p>
    </>
  );
}
